import { useMemo, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { ppaSimulationUrls } from 'config/urls';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';
import {
    DEFAULT_SIMULATOR_TABLE_DIMENSIONS,
    buildSimulatorTableRequestPayload,
    type SimulatorTableColumnFilterPayload,
    type SimulatorTableSearchInput,
    type SimulatorTableSortPayload,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilter.helpers';
import type { SimulatorAppliedFilters } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import type { PPAScenarioColumnVisibilityInput } from 'types/ppaViews';
import type { ColumnFilterSortValue } from './ColumnFilterSortPanel';
import {
    SIMULATION_TABLE_QUERY_KEY,
    buildSimulationTableUiConfig,
    collectHierarchyAttributeTypes,
    collectVisibleKpiColumns,
    mapSimulationTableRows,
    resolveVisibleAttributeColumns,
    type SimulationTableApiResponse,
    type SimulationTableGroupByItem,
} from './simulationTable.helpers';

export type { GenericScenarioTableUiConfig } from './genericScenarioTable.types';

const EMPTY_APPLIED_FILTERS: SimulatorAppliedFilters = {
    categorySelections: {},
    showOnly: {
        edited: false,
        impacted: false,
        editedFilters: [],
    },
};

const emptyResolvedFilters = (): Record<string, number[]> =>
    Object.fromEntries(DEFAULT_SIMULATOR_TABLE_DIMENSIONS.map((dimension) => [dimension, []]));

export type UseGenericAntdScenarioTableArgs = {
    projectId?: number | string | null;
    scenarioId?: number | string | null;
    resolvedFilters?: Record<string, number[]> | null;
    appliedFilters?: SimulatorAppliedFilters;
    searchInput?: SimulatorTableSearchInput;
    nestDimensions?: boolean;
    columnVisibility?: PPAScenarioColumnVisibilityInput[];
    groupBy?: SimulationTableGroupByItem[];
    activeLensId?: number | null;
};

export const useGenericAntdScenarioTable = ({
    projectId,
    scenarioId,
    resolvedFilters,
    appliedFilters,
    searchInput,
    nestDimensions = false,
    columnVisibility,
    groupBy,
    activeLensId,
}: UseGenericAntdScenarioTableArgs = {}) => {
    const { simulationTableConfig } = usePriceSimulatorV3Context();
    const canFetch = projectId != null && projectId !== '' && scenarioId != null && scenarioId !== '';
    const requestFilters = resolvedFilters ?? emptyResolvedFilters();
    const requestAppliedFilters = appliedFilters ?? EMPTY_APPLIED_FILTERS;
    const [tableSort, setTableSort] = useState<SimulatorTableSortPayload | null>(null);
    const [columnFilterSortByKey, setColumnFilterSortByKey] = useState<Record<string, ColumnFilterSortValue>>({});
    const columnFilters = useMemo<SimulatorTableColumnFilterPayload[]>(
        () =>
            Object.entries(columnFilterSortByKey)
                .filter(([, value]) => value.selectedValues.length > 0)
                .map(([id, value]) => ({ id, values: value.selectedValues })),
        [columnFilterSortByKey],
    );

    const { data, isFetching } = useQuery<SimulationTableApiResponse>({
        queryKey: [
            SIMULATION_TABLE_QUERY_KEY,
            String(projectId ?? ''),
            String(scenarioId ?? ''),
            JSON.stringify(requestFilters),
            JSON.stringify(requestAppliedFilters.showOnly),
            JSON.stringify(searchInput ?? null),
            JSON.stringify(tableSort),
            JSON.stringify(columnFilters),
        ],
        enabled: canFetch,
        queryFn: async () => {
            const payload = buildSimulatorTableRequestPayload({
                projectId: projectId as number | string,
                scenarioId: scenarioId as number | string,
                resolvedFilters: requestFilters,
                appliedFilters: requestAppliedFilters,
                searchInput,
                sort: tableSort,
                columnFilters,
            });
            const response = await axios.post(ppaSimulationUrls.simulatorTableUrl, payload);
            return (response.data ?? {}) as SimulationTableApiResponse;
        },
    });

    const apiRows = useMemo(() => data?.data ?? [], [data?.data]);
    const hierarchyAttributeTypes = useMemo(() => collectHierarchyAttributeTypes(apiRows), [apiRows]);
    const visibleAttributeColumns = useMemo(
        () =>
            resolveVisibleAttributeColumns({
                columnVisibility,
                groupBy,
                activeLensId,
                hierarchyAttributeTypes,
            }),
        [activeLensId, columnVisibility, groupBy, hierarchyAttributeTypes],
    );
    const visibleKpiColumns = useMemo(() => collectVisibleKpiColumns(apiRows), [apiRows]);
    const sourceRows = useMemo(
        () => mapSimulationTableRows(apiRows, simulationTableConfig, visibleAttributeColumns, visibleKpiColumns),
        [apiRows, simulationTableConfig, visibleAttributeColumns, visibleKpiColumns],
    );

    const rowCount = sourceRows.length;
    const limit = data?.limit ?? simulationTableConfig.pagination.limit;
    const total = data?.total ?? rowCount;
    const recordLabel = rowCount > 0 ? `Showing ${Math.min(rowCount, limit)} of ${total} Records` : '';

    const tableUiConfig = useMemo(
        () =>
            buildSimulationTableUiConfig({
                tableConfig: simulationTableConfig,
                nestDimensions,
                recordLabel,
                valuePrefix: String(sourceRows[0]?.valuePrefix ?? ''),
                visibleAttributeColumns,
                visibleKpiColumns,
            }),
        [nestDimensions, recordLabel, simulationTableConfig, sourceRows, visibleAttributeColumns, visibleKpiColumns],
    );

    return {
        tableUiConfig,
        sourceRows,
        isLoading: canFetch && isFetching,
        tableSort,
        setTableSort,
        columnFilterSortByKey,
        setColumnFilterSortByKey,
    };
};
